<template>
  <v-container
    id="templateSections"
    class="pa-0"
  >
    <base-material-card
      :color="color"
      :icon="icon"
      inline
    >
      <template v-slot:after-heading>
        <v-toolbar
          flat
          color="white"
        >
          <v-toolbar-title
            class="display-2 font-weight-light d-none d-sm-flex"
          >
            Templates
          </v-toolbar-title>
        </v-toolbar>
      </template>
      <v-progress-linear
        v-show="loading"
        indeterminate
        :color="color"
      />
      <v-row
        v-if="!noTemplatesFound && !loading"
      >
        <v-col
          cols="12"
          md="8"
        >
          <v-btn
            class="mx-2"
            fab
            dark
            small
            outlined
            color="primary"
            @click="toggleExpansion"
          >
            <v-icon
              v-if="expanded"
              dark
            >
              mdi-minus
            </v-icon>
            <v-icon
              v-if="!expanded"
              dark
            >
              mdi-plus
            </v-icon>
          </v-btn>
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            class="ml-auto"
            label="Search"
            hide-details
            single-line
          />
        </v-col>
      </v-row>
      <v-alert
        v-if="noTemplatesFound"
        type="info"
      >
        No templates found
      </v-alert>
      <v-expansion-panels
        v-model="templatePanels"
        popout
        hover
        focusable
        accordion
        multiple
      >
        <v-expansion-panel
          v-for="(template,templateKey) in filtered"
          :key="templateKey"
        >
          <v-expansion-panel-header
            color="green darken-1"
            ripple
          >
            <v-subheader
              class="display-2"
            >
              {{ template.title }}
            </v-subheader>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-expansion-panels
              v-model="sectionPanels[template.productTemplateId]"
              popout
              hover
              focusable
              accordion
              multiple
            >
              <v-expansion-panel
                v-for="(section,sectionKey) in template.sections"
                :key="sectionKey"
              >
                <v-expansion-panel-header
                  color="green lighten-5"
                  ripple
                >
                  <v-subheader
                    class="display-2"
                  >
                    {{ section.templateSection }}
                  </v-subheader>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div
                    v-if="section.answerMulti"
                  >
                    <template-edit
                      :key="lastAddNew"
                      :answers="section.currentEdit"
                      :loading="section.saving"
                      :disabled="disabled"
                      :saving="saving"
                      button-label="Add New"
                      @save="addNew(section, arguments)"
                    />
                    <div
                      v-for="(answerGroup,answerGroupKey) in section.answerGroups"
                      :key="answerGroupKey"
                      class="multi-section"
                    >
                      <template-edit
                        :key="lastEditMulti"
                        :answers="answerGroup.answers"
                        :loading="answerGroup.saving"
                        :disabled="disabled"
                        :saving="saving"
                        button-label="Save"
                        delete-label="Delete"
                        @delete="remove(section, arguments)"
                        @save="editAnswerGroup(section, arguments)"
                      />
                    </div>
                  </div>
                  <template-edit
                    v-if="!section.answerMulti"
                    :key="lastSectionChange"
                    :answers="section.answers"
                    :loading="section.saving"
                    :disabled="disabled"
                    :saving="saving"
                    delete-label="Clear"
                    @delete="remove(section, arguments)"
                    @save="saveSection(section, arguments)"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </base-material-card>
    <v-dialog
      v-model="deleteDialog"
      persistent
      max-width="290"
    >
      <v-card>
        <v-progress-linear
          v-show="deleting"
          indeterminate
          :color="color"
        />
        <v-card-title
          class="headline"
        >
          Are You Sure?
        </v-card-title>
        <v-card-text>
          Are you sure you wish to delete this set of answers?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="red darken-1"
            text
            @click="confirmDelete()"
          >
            Yes
          </v-btn>
          <v-btn
            color="green darken-1"
            text
            @click="cancelDelete()"
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <base-material-snackbar
      v-model="displayError"
      type="error"
      v-bind="{
        top: true,
        right: true
      }"
    >
      {{ errorMessage }}
    </base-material-snackbar>
    <base-material-snackbar
      v-model="displaySuccess"
      type="success"
      v-bind="{
        top: true,
        right: true
      }"
    >
      {{ successMessage }}
    </base-material-snackbar>
  </v-container>
</template>

<script>
  import TroodyApi from '@/services/troody/TroodyApi'

  export default {
    name: 'TemplateSections',

    components: {
      TemplateEdit: () => import('./TemplateEdit'),
    },

    props: {
      color: {
        type: String,
        default: 'green',
      },
      icon: {
        type: String,
        default: 'mdi-comment-question',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      openEnrollment: {
        type: Object,
        default: null,
      },
    },

    data: () => ({
      fetchedData: false,
      loadingAnswers: false,

      errorMessage: null,
      displayError: false,

      successMessage: null,
      displaySuccess: false,
      lastSectionChange: Date.now(),
      lastAddNew: Date.now(),
      lastEditMulti: Date.now(),

      search: null,
      itemToDelete: null,
      deleting: false,

      templatePanels: [],
      sectionPanels: {},
      expanded: false,
      saving: false,

      templates: null,
      answers: [],
      answersResource: new TroodyApi('oeTemplateAnswer/'),
    }),

    computed: {
      openEnrollmentId () {
        return (this.openEnrollment || {}).id || null
      },

      loading () {
        return this.loadingAnswers
      },

      noTemplatesFound () {
        return (!this.loading && this.fetchedData && (this.templates || []).length <= 0)
      },

      filtered () {
        return this.filteredTemplates()
      },

      deleteDialog () {
        return this.itemToDelete !== null
      },
    },

    created () {
      this.loadAnswers()
    },

    methods: {
      filteredTemplates () {
        let results = this.templates

        if (this.templates && this.templates.length && this.search) {
          const fixedSearch = this.search.toUpperCase()

          this.templates.forEach(template => {
            template.matches = ((template.title || '').toUpperCase().includes(fixedSearch))
            if (template.sections && template.sections.length) {
              template.sections.forEach(section => {
                section.matches = ((section.templateSection || '').toUpperCase().includes(fixedSearch))
                if (section.matches) {
                  template.matches = true
                }

                let answersToSearch = section.answers
                if (section.answerMulti) {
                  if (section.answerGroups && section.answerGroups.length) {
                    answersToSearch = []
                    section.answerGroups.forEach(answerGroup => {
                      answersToSearch = answersToSearch.concat(answerGroup.answers || [])
                    })
                  }
                }

                if (answersToSearch && answersToSearch.length) {
                  answersToSearch.forEach(answer => {
                    answer.matches = true // Assume the match and prove if false
                  })
                }

                if (answersToSearch && answersToSearch.length) {
                  const matchingAnswers = answersToSearch.filter(answer => {
                    const combined = (answer.question || '').toUpperCase() + (answer.answer || '').toUpperCase()
                    return combined.includes(fixedSearch)
                  })

                  const nonMatchingAnswers = answersToSearch.filter(answer => {
                    const combined = (answer.question || '').toUpperCase() + (answer.answer || '').toUpperCase()
                    return !combined.includes(fixedSearch)
                  })

                  if (matchingAnswers && matchingAnswers.length) {
                    template.matches = true
                    section.matches = true
                    // Now mark the non matching as false
                    nonMatchingAnswers.forEach(answer => {
                      answer.matches = false
                    })
                  }
                }
              })
            }
          })

          results = []

          const matchingTemplates = this.templates.filter(template => template.matches)
          if (matchingTemplates && matchingTemplates.length) {
            matchingTemplates.forEach(t => {
              const template = Object.assign({}, t)

              if (template.sections && template.sections.length) {
                let filteredSections = null
                const matchingSections = template.sections.filter(section => section.matches)
                if (matchingSections && matchingSections.length) {
                  filteredSections = []
                  matchingSections.forEach(section => {
                    filteredSections.push(Object.assign({}, section))
                  })
                }

                template.sections = filteredSections

                if (template.sections && template.sections.length) {
                  template.sections.forEach(section => {
                    if (section.answerMulti) {
                      if (section.answerGroups && section.answerGroups.length) {
                        let filteredAnswerGroups = null
                        const matchingAnswerGroups = section.answerGroups.filter(answerGroup => {
                          return (answerGroup.answers || []).filter(answer => answer.matches)
                        })
                        if (matchingAnswerGroups && matchingAnswerGroups.length) {
                          filteredAnswerGroups = []
                          matchingAnswerGroups.forEach(answerGroup => {
                            filteredAnswerGroups.push(Object.assign({}, answerGroup))
                          })
                        }
                        section.answerGroups = filteredAnswerGroups

                        if (section.answerGroups && section.answerGroups.length) {
                          section.answerGroups.forEach(answerGroup => {
                            let filteredAnswers = null
                            const matchingAnswerGroupAnswers = (answerGroup.answers || []).filter(answer => answer.matches)
                            if (matchingAnswerGroupAnswers && matchingAnswerGroupAnswers.length) {
                              filteredAnswers = []
                              matchingAnswerGroupAnswers.forEach(answer => {
                                filteredAnswers.push(Object.assign({}, answer))
                              })
                            }
                            answerGroup.answers = filteredAnswers
                          })
                        }
                      }
                    } else {
                      let filteredSectionAnswers = null
                      const matchingAnswers = section.answers.filter(answer => answer.matches)
                      if (matchingAnswers && matchingAnswers.length) {
                        filteredSectionAnswers = []
                        matchingAnswers.forEach(answer => {
                          filteredSectionAnswers.push(Object.assign({}, answer))
                        })
                        section.answers = filteredSectionAnswers
                      }
                    }
                  })
                }

                results.push(template)
              }
            })
          }
        }

        return results
      },

      toggleExpansion () {
        if (this.expanded) {
          this.collapseAll()
        } else {
          this.expandAll()
        }
        this.expanded = !this.expanded
      },

      collapseAll () {
        this.sectionPanels = {}
        this.templatePanels = []
        if (this.templates && this.templates.length) {
          this.templates.forEach((template) => {
            this.sectionPanels[template.productTemplateId] = []
          })
        }
      },

      expandAll () {
        this.templatePanels = [...Array(this.templates).keys()].map((k, i) => i)
        if (this.templates && this.templates.length) {
          this.templates.forEach((template) => {
            this.sectionPanels[template.productTemplateId] = template.sections.map((k, i) => i)
          })
        }
      },

      remove (section, args) {
        this.itemToDelete = {
          section: section,
          answers: args[0],
        }
      },

      removeAnswers (section, answers) {
        this.$emit('answers:removed', answers)

        // Get a distinct list of the answer group id's that were removed
        const answerGroupIdList = this.getAnswerGroupIdList(answers)

        if (section.answerMulti) {
          // Only continue with answer groups that aren't in the list that was deleted
          if (answerGroupIdList && answerGroupIdList.length && section && section.answerGroups && section.answerGroups.length) {
            section.answerGroups = section.answerGroups.filter((i) => { return !answerGroupIdList.includes(i.answerGroupId) })
          }
        } else {
          answers.forEach(answer => {
            answer.oeTemplateId = 0
            answer.answerGroupId = null
            answer.answer = null
          })
        }
      },

      prepareForSave (answers) {
        let results = answers
        if (answers && answers.length) {
          results = answers.map((answer) => {
            const output = Object.assign({}, answer)
            if (output.answer !== null && output.answer !== undefined) {
              output.answer = output.answer.toString()
            }
            return output
          })
        }
        return results
      },

      confirmDelete () {
        console.log('delete')
        console.log(this.itemToDelete)

        const answers = (this.itemToDelete || {}).answers
        if (answers) {
          this.deleting = true

          // Post only the items to save
          this.answersResource.post(this.prepareForSave(answers), 'deleteAnswers')
            .then((data) => {
              const section = (this.itemToDelete || {}).section
              this.removeAnswers(section, answers)

              this.deleting = false
              this.itemToDelete = null
            })
            .catch((error) => {
              this.deleting = false
              this.showError(error, 'Failed to delete!')
            })
        }
      },

      cancelDelete () {
        this.itemToDelete = null
      },

      setCurrentEdit (section) {
        // Clone the questions array
        const questions = (section || {}).questions || []
        if (questions.length) {
          const currentEdit = []
          questions.forEach(question => {
            currentEdit.push(Object.assign({}, question))
          })
          section.currentEdit = currentEdit
        }
      },

      prepareForEdit () {
        if (this.templates && this.templates.length) {
          this.templates.forEach(template => {
            if (template.sections && template.sections.length) {
              template.sections.forEach(section => {
                section.saving = false
                if (section.answerMulti) {
                  this.setCurrentEdit(section)
                  if (section.answerGroups && section.answerGroups.length) {
                    section.answerGroups.forEach(answerGroup => {
                      answerGroup.saving = false
                    })
                  }
                }
              })
            }
          })
        }
      },

      getAnswerGroupIdList (answers) {
        const answerGroupIdList = (answers && answers.length) ? answers.map(i => { return i.answerGroupId }).filter((v, i, a) => a.indexOf(v) === i) : null
        return answerGroupIdList
      },

      addNew (section, args) {
        const modifiedAnswers = args[1]
        if (modifiedAnswers && modifiedAnswers.length) {
          section.saving = true
          this.lastAddNew = Date.now() // This is such a stupid hack and is making me hate vue
          this.saveAnswers(args[0])
            .then(addedItems => {
              section.saving = false
              if (addedItems && addedItems.length) {
                const answerGroupIdList = this.getAnswerGroupIdList(addedItems)
                if (answerGroupIdList && answerGroupIdList.length) {
                  if (!section.answerGroups) {
                    section.answerGroups = []
                  }
                  answerGroupIdList.forEach((answerGroupId) => {
                    section.answerGroups.push({
                      answerGroupId: answerGroupId,
                      answers: addedItems.filter((i) => { return i.answerGroupId === answerGroupId }),
                    })
                  })
                }
                this.setCurrentEdit(section)
                this.lastAddNew = Date.now() // This is such a stupid hack and is making me hate vue
              }
            })
        }
      },

      setAnswerGroupSaving (answers, saving) {
        if (this.templates && this.templates.length) {
          const answerGroupIdList = this.getAnswerGroupIdList(answers)
          if (answerGroupIdList && answerGroupIdList.length) {
            this.templates.forEach(template => {
              if (template.sections && template.sections.length) {
                const multiSections = template.sections.filter(section => section.answerMulti)
                if (multiSections && multiSections.length) {
                  multiSections.forEach(section => {
                    if (section.answerGroups) {
                      const answerGroupsToMark = section.answerGroups.filter((answerGroup) => {
                        return answerGroupIdList.includes(answerGroup.answerGroupId)
                      })
                      if (answerGroupsToMark && answerGroupsToMark.length) {
                        answerGroupsToMark.forEach(answerGroup => {
                          answerGroup.saving = saving
                        })
                      }
                    }
                  })
                }
              }
            })
          }
        }
      },

      editAnswerGroup (section, args) {
        const modifiedAnswers = args[1]
        if (modifiedAnswers && modifiedAnswers.length) {
          this.setAnswerGroupSaving(modifiedAnswers, true)
          this.lastEditMulti = Date.now() // This is such a stupid hack and is making me hate vue
          this.saveAnswers(args[0], modifiedAnswers)
            .then(data => {
              this.setAnswerGroupSaving(modifiedAnswers, false)
              this.lastEditMulti = Date.now() // This is such a stupid hack and is making me hate vue
            })
        }
      },

      saveSection (section, args) {
        const modifiedAnswers = args[1]
        if (modifiedAnswers && modifiedAnswers.length) {
          section.saving = true
          this.saveAnswers(args[0], modifiedAnswers)
            .then(data => {
              section.saving = false
              section.answers = data
              this.lastSectionChange = Date.now() // This is such a stupid hack and is making me hate vue
            })
        }
      },

      saveAnswers (answers, toSave) {
        const promise = new Promise((resolve, reject) => {
          this.saving = true

          // If they didn't single out specific answers to deliver, deliver all of them
          const payload = toSave || answers

          // Post only the items to save
          this.answersResource.post(this.prepareForSave(payload), 'saveAnswers')
            .then((data) => {
              this.saving = false

              // The server gives back answers that were modified on the server
              // We need to modify the original set to match
              if (data && data.length) {
                answers.forEach((localAnswer) => {
                  // See if the server returned modifications for the current answer we are looking at
                  var serverAnswer = data.filter((searched) => {
                    // Local answer id is actually the dynamic oeTemplateAnswerID concept
                    return localAnswer.id === searched.oeTemplateAnswerId
                  })
                  if (serverAnswer && serverAnswer.length) {
                    // The local answer is no longer in a modified state
                    localAnswer.modified = false
                    localAnswer.messages = []

                    // Server is passing us back the actual oeTemplateId as "id"
                    localAnswer.oeTemplateId = serverAnswer[0].id

                    // Server also assigned a group id
                    localAnswer.answerGroupId = serverAnswer[0].answerGroupId
                  }
                })
              }
              this.successMessage = 'Saved successfully!'
              this.displaySuccess = true
              this.$emit('answers:saved', answers)
              resolve(answers)
            })
            .catch((error) => {
              this.saving = false
              this.showError(error, 'Failed to save')
            })
        })

        return promise
      },

      hasChanges (filter) {
        let hasChanges = false

        if (this.answers && this.answers.length) {
          const modifiedFields = this.answers.filter((item) => filter(item))
          hasChanges = (modifiedFields && modifiedFields.length)
        }

        return hasChanges
      },

      onChange (answer) {
        answer.modified = true
        answer.messages = ['Modified']
        this.$emit('change', answer)
        this.$forceUpdate()
        console.log('changed')
        console.log(answer)
      },

      showError (error, msg) {
        console.log(error)
        this.errorMessage = (error || msg)
        this.displayError = true
      },

      loadAnswers () {
        this.loadingAnswers = true
        this.answersResource.getFrom('structured/', this.openEnrollmentId)
          .then(data => {
            this.fetchedData = true
            this.loadingAnswers = false
            this.templates = data
            this.prepareForEdit()
          })
          .catch((error) => {
            this.fetchedData = true
            this.loadingAnswers = false
            this.templates = null
            this.showError(error, 'Failed to load answers!')
          })
      },
    },
  }
</script>

<style lang="sass">
  .multi-section
    border-width: 3px
    border-style: solid
    border-color: rgba(0, 255, 0, 0.3) !important
    border-radius: 10px !important
    background-color: rgba(0, 255, 0, 0.02) !important
    padding-left: 10px !important
    padding-right: 10px !important
    margin-bottom: 10px !important
</style>
